import { useMemo, useCallback } from 'react';
import { ApiKeyUsageDetail } from '../../../../utils/openai-api';
import { EmployeeRecord } from '@/utils/employee-indexeddb';

import { UseFilteredDataParams } from './types';
import { findEmployeeForApiKeyFn } from './findEmployee';
import { computeFilteredData } from './computeFilteredData';
import { getModelUsageDisplay } from '../../utils/modelUsageDisplay';

// ── Hook ───────────────────────────────────────────────

export function useFilteredData({
  data,
  getDateRange,
  excludedModels,
  selectedWorkspace,
  selectedModel,
  filter,
  selectedDepartment,
  normalizedDepartmentFilter,
  selectedRole,
  employeeNameFilter,
  employeeData,
  dataSource,
  modelSortKey,
  modelSortDir,
}: UseFilteredDataParams) {

  const findEmployeeForApiKey = useCallback(
    (apiKey: ApiKeyUsageDetail): EmployeeRecord | undefined => {
      return findEmployeeForApiKeyFn(apiKey, employeeData);
    },
    [employeeData]
  );

  // Filter data by date range and excluded models
  const filteredData = useMemo(
    () => computeFilteredData({
      data,
      getDateRange,
      excludedModels,
      selectedWorkspace,
      selectedModel,
      filter,
      normalizedDepartmentFilter,
      selectedRole,
      employeeNameFilter,
      employeeData,
      dataSource,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, getDateRange, excludedModels, selectedWorkspace, selectedModel, filter, selectedDepartment, selectedRole, employeeNameFilter, employeeData, dataSource]
  );

  const sortedModelUsage = useMemo(() => {
    if (!filteredData?.modelUsage) return [];
    const getCacheRate = (m: { cachedTokens: number; contextTokens: number }) =>
      m.contextTokens > 0 ? m.cachedTokens / m.contextTokens : 0;
    return [...filteredData.modelUsage].sort((a, b) => {
      let cmp = 0;
      switch (modelSortKey) {
        case 'model': cmp = a.model.localeCompare(b.model); break;
        case 'contextTokens': cmp = a.contextTokens - b.contextTokens; break;
        case 'generatedTokens': cmp = a.generatedTokens - b.generatedTokens; break;
        case 'cachedTokens': cmp = a.cachedTokens - b.cachedTokens; break;
        case 'cacheRate': cmp = getCacheRate(a) - getCacheRate(b); break;
        case 'cost': cmp = a.cost - b.cost; break;
        case 'numberOfRequests': cmp = (getModelUsageDisplay(a).quantity ?? -1) - (getModelUsageDisplay(b).quantity ?? -1); break;
        case 'lastUsed': cmp = (a.lastUsed ?? '').localeCompare(b.lastUsed ?? ''); break;
        default: cmp = 0;
      }
      return modelSortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredData?.modelUsage, modelSortKey, modelSortDir]);

  const dataWindow = filteredData?.dataWindow || data?.dataWindow;
  const cacheTimestamp = filteredData?.cacheTimestamp || data?.cacheTimestamp;

  const coverageInfo = useMemo(() => {
    if (!dataWindow || !dataWindow.expectedHours) return null;
    if (dataWindow.expectedHours <= 0) return null;

    const percentage = Math.max(0, Math.min(100, Math.round((dataWindow.coverageHours / dataWindow.expectedHours) * 100)));
    const formatHours = (value: number) => {
      return Math.abs(value - Math.round(value)) < 0.01
        ? Math.round(value).toString()
        : value.toFixed(2);
    };

    return {
      coverageHoursLabel: formatHours(dataWindow.coverageHours),
      expectedHoursLabel: formatHours(dataWindow.expectedHours),
      percentage
    };
  }, [dataWindow]);

  const availableWorkspaces = useMemo(() => {
    if (!data || !data.projects) {
      return [];
    }
    const activeProjects = data.projects
      .filter(p => p.status === 'active')
      .map(p => ({ id: p.id, name: p.name, provider: p.provider }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return activeProjects;
  }, [data]);

  const availableModels = useMemo(() => {
    if (!data || !data.modelUsage) {
      return [];
    }
    const models = data.modelUsage
      .filter(m => !excludedModels.includes(m.model))
      .map(m => m.model)
      .sort((a, b) => a.localeCompare(b));
    return models;
  }, [data, excludedModels]);

  return {
    filteredData,
    sortedModelUsage,
    dataWindow,
    cacheTimestamp,
    coverageInfo,
    availableWorkspaces,
    availableModels,
    findEmployeeForApiKey,
  };
}
